import { Address } from '@guardfinder/address';

import { UniformProvidedByOptions } from '../../../../../shared';
import { JOB_TYPES, JobTypes, RepeatCustomTypes, RepeatCycleTypes } from '../../../../../types';

/**
 * Defines the complete schema for job creation state.
 * This type contains all fields that need to be collected during the job creation flow.
 * Fields are organized by the step in which they are typically collected.
 */
export type StateSchema = {
    // Date and time step
    jobType: JobTypes; // Type of job (one-time or ongoing)
    startDate: string | null; // Start date in ISO format
    startTime: string | null; // Start time in HH:MM format
    endDate: string | null; // End date in ISO format
    numberOfGuards: number | null; // Number of guards needed
    numberOfHoursPerDay: number | null; // Hours per day each guard will work

    // Repeat settings (for ongoing jobs)
    repeatCycle: RepeatCycleTypes | null; // How often the job repeats
    repeatCycleCustomType: RepeatCustomTypes | null; // Custom repeat type (days, weeks, months)
    repeatDays: string[] | null; // Days of the week for weekly repeats
    repeatCycleFrequencyAmount: number | null; // Frequency amount for custom repeats
    repeatEndDate: string | null; // End date for repeating jobs

    // Event information step
    eventType: string | null; // Type of event requiring security
    eventLocationAddress: Address | null; // Location address for the job

    // Contact information step
    primaryContactName: string | null; // Name of primary contact
    primaryContactPhone: string | null; // Phone number of primary contact
    primaryContactEmail: string | null; // Email of primary contact

    // Job details step
    jobDescription: string | null; // Description of the job
    previousSecurityIssues: string | null; // Previous security issues at the location
    emergencyProcedures: string | null; // Emergency procedures for guards
    guardPrimaryDuties: string | null; // Primary duties for guards

    // Uniform requirements
    requireUniform: boolean | null; // Whether uniforms are required
    uniformType: string | null; // Type of uniform required
    uniformProvidedBy: UniformProvidedByOptions | null; // Who provides the uniform

    // Navigation state
    fromReviewScreen: boolean; // Whether navigation is from review screen

    // Agreement to Terms of Use and Privacy Policy
    // User should agree to this before data is posted to the back-end
    agreeToUPP: boolean;
};

/**
 * Type representing any key from the StateSchema.
 * Used for type-safe access to state properties.
 */
export type StateKey = keyof StateSchema;

/**
 * Abstract class defining the interface for job creation flow state management.
 *
 * This class serves as the contract for state management services in the job creation flow.
 * It provides:
 * - Promise-based getter and setter methods for all job flow state fields
 * - Type-safe access to state properties
 * - Abstraction over the actual state storage mechanism
 * - Support for asynchronous state operations (like loading from/saving to storage)
 *
 * The class follows a consistent pattern:
 * - Protected properties store the actual state values
 * - Public getter methods return promises that resolve to the current values
 * - Public setter methods update the values and return promises that resolve when complete
 * - Protected getValue/setValue methods handle the common logic for all properties
 *
 * This design allows implementations to:
 * - Add persistence mechanisms (like storage or server synchronization)
 * - Implement state restoration logic
 * - Add validation or transformation logic
 * - Control when and how state changes are processed
 *
 * Implementations of this class handle the details of state persistence and restoration.
 */
export abstract class WebformCreateJobFlowState {
    protected jobTypeValue: JobTypes = JOB_TYPES.ONE_TIME;
    protected startDateValue: string | null = null;
    protected startTimeValue: string | null = null;
    protected endDateValue: string | null = null;
    protected numberOfGuardsValue: number | null = null;
    protected numberOfHoursPerDayValue: number | null = null;
    protected repeatCycleValue: RepeatCycleTypes | null = null;
    protected repeatCycleCustomTypeValue: RepeatCustomTypes | null = null;
    protected repeatDaysValue: string[] | null = null;
    protected repeatCycleFrequencyAmountValue: number | null = null;
    protected repeatEndDateValue: string | null = null;
    protected eventTypeValue: string | null = null;
    protected eventLocationAddressValue: Address | null = null;
    protected primaryContactNameValue: string | null = null;
    protected primaryContactPhoneValue: string | null = null;
    protected primaryContactEmailValue: string | null = null;
    protected jobDescriptionValue: string | null = null;
    protected previousSecurityIssuesValue: string | null = null;
    protected emergencyProceduresValue: string | null = null;
    protected guardPrimaryDutiesValue: string | null = null;
    protected requireUniformValue: boolean | null = null;
    protected uniformTypeValue: string | null = null;
    protected uniformProvidedByValue: UniformProvidedByOptions | null = null;
    protected fromReviewScreenValue: boolean = false;
    protected agreeToUPPValue: boolean = false;

    /**
     * ===== DATE AND TIME GETTERS =====
     * Methods to retrieve date, time, and scheduling information
     */

    /**
     * Gets the job type (one-time or ongoing).
     * @returns Promise resolving to the job type
     */
    jobType(): Promise<JobTypes> {
        return this.getValue('jobType');
    }

    /**
     * Gets the start date of the job.
     * @returns Promise resolving to the start date in ISO format (YYYY-MM-DD)
     */
    startDate(): Promise<string | null> {
        return this.getValue('startDate');
    }

    /**
     * Gets the start time of the job.
     * @returns Promise resolving to the start time in HH:MM format
     */
    startTime(): Promise<string | null> {
        return this.getValue('startTime');
    }

    /**
     * Gets the end date of the job.
     * For one-time jobs, this may be the same as the start date.
     * @returns Promise resolving to the end date in ISO format (YYYY-MM-DD)
     */
    endDate(): Promise<string | null> {
        return this.getValue('endDate');
    }

    /**
     * Gets the number of guards required for the job.
     * @returns Promise resolving to the number of guards
     */
    numberOfGuards(): Promise<number | null> {
        return this.getValue('numberOfGuards');
    }

    /**
     * Gets the number of hours each guard will work per day.
     * @returns Promise resolving to the number of hours per day
     */
    numberOfHoursPerDay(): Promise<number | null> {
        return this.getValue('numberOfHoursPerDay');
    }

    /**
     * Gets the repeat cycle for ongoing jobs.
     * Defines how often the job repeats (daily, weekly, monthly, etc.).
     * @returns Promise resolving to the repeat cycle type
     */
    repeatCycle(): Promise<RepeatCycleTypes | null> {
        return this.getValue('repeatCycle');
    }

    /**
     * Gets the custom repeat type for jobs with custom repeat cycles.
     * Used when repeatCycle is set to 'custom'.
     * @returns Promise resolving to the custom repeat type (days, weeks, months)
     */
    repeatCycleCustomType(): Promise<RepeatCustomTypes | null> {
        return this.getValue('repeatCycleCustomType');
    }

    /**
     * Gets the days of the week for weekly repeating jobs.
     * @returns Promise resolving to an array of day names
     */
    repeatDays(): Promise<string[] | null> {
        return this.getValue('repeatDays');
    }

    /**
     * Gets the frequency amount for custom repeat cycles.
     * For example, "every 2 weeks" would have a frequency amount of 2.
     * @returns Promise resolving to the frequency amount
     */
    repeatCycleFrequencyAmount(): Promise<number | null> {
        return this.getValue('repeatCycleFrequencyAmount');
    }

    /**
     * Gets the end date for repeating jobs.
     * Defines when the recurring job schedule ends.
     * @returns Promise resolving to the repeat end date in ISO format (YYYY-MM-DD)
     */
    repeatEndDate(): Promise<string | null> {
        return this.getValue('repeatEndDate');
    }

    /**
     * ===== EVENT INFO GETTERS =====
     * Methods to retrieve information about the event
     */

    /**
     * Gets the event type.
     * @returns Promise resolving to the event type
     */
    eventType(): Promise<string | null> {
        return this.getValue('eventType');
    }

    /**
     * Gets the location address for the job event.
     * Contains full address information including street, city, state, and zip.
     * @returns Promise resolving to the event location address object
     */
    eventLocationAddress(): Promise<Address | null> {
        return this.getValue('eventLocationAddress');
    }

    /**
     * ===== PRIMARY CONTACT INFO GETTERS =====
     * Methods to retrieve information about the primary contact person
     */

    /**
     * Gets the primary contact's name.
     * @returns Promise resolving to the primary contact's name
     */
    primaryContactName(): Promise<string | null> {
        return this.getValue('primaryContactName');
    }

    /**
     * Gets the primary contact's phone number.
     * Used for communication regarding the job.
     * @returns Promise resolving to the primary contact's phone number
     */
    primaryContactPhone(): Promise<string | null> {
        return this.getValue('primaryContactPhone');
    }

    /**
     * Gets the primary contact's email address.
     * Used for communication and notifications regarding the job.
     * @returns Promise resolving to the primary contact's email address
     */
    primaryContactEmail(): Promise<string | null> {
        return this.getValue('primaryContactEmail');
    }

    /**
     * ===== JOB INFO GETTERS =====
     * Methods to retrieve detailed information about the job requirements
     */

    /**
     * Gets the job description.
     * @returns Promise resolving to the job description
     */
    jobDescription(): Promise<string | null> {
        return this.getValue('jobDescription');
    }

    /**
     * Gets information about previous security issues at the location.
     * Helps guards prepare for potential recurring issues.
     * @returns Promise resolving to the previous security issues description
     */
    previousSecurityIssues(): Promise<string | null> {
        return this.getValue('previousSecurityIssues');
    }

    /**
     * Gets emergency procedures for guards to follow.
     * Provides instructions for handling emergency situations during the job.
     * @returns Promise resolving to the emergency procedures description
     */
    emergencyProcedures(): Promise<string | null> {
        return this.getValue('emergencyProcedures');
    }

    /**
     * Gets the primary duties expected of guards during the job.
     * Defines the main responsibilities and tasks for security personnel.
     * @returns Promise resolving to the guard primary duties description
     */
    guardPrimaryDuties(): Promise<string | null> {
        return this.getValue('guardPrimaryDuties');
    }

    /**
     * Gets whether uniforms are required for guards on this job.
     * Determines if uniform information needs to be collected.
     * @returns Promise resolving to true if uniforms are required, false otherwise
     */
    requireUniform(): Promise<boolean | null> {
        return this.getValue('requireUniform');
    }

    /**
     * Gets the type of uniform required for the job.
     * Only relevant if requireUniform is true.
     * @returns Promise resolving to the uniform type description
     */
    uniformType(): Promise<string | null> {
        return this.getValue('uniformType');
    }

    /**
     * Gets who will provide the uniforms for the job.
     * Only relevant if requireUniform is true.
     * @returns Promise resolving to the uniform provider option
     */
    uniformProvidedBy(): Promise<UniformProvidedByOptions | null> {
        return this.getValue('uniformProvidedBy');
    }

    /**
     * ===== NAVIGATION FLAGS GETTERS =====
     * Methods to retrieve flags that control navigation flow
     */

    /**
     * Gets whether the current navigation originated from the review screen.
     * Used to determine if the user should be returned to the review screen after editing.
     *
     * @returns Promise resolving to true if navigation is from review screen
     */
    fromReviewScreen(): Promise<boolean> {
        return this.getValue('fromReviewScreen');
    }

    /**
     * ===== AGREE TERMS OF USE AND PRIVACY POLICY GETTERS =====
     * Methods to retrieve information about the agreement to ToU and PP
     */

    /**
     * Gets the event type.
     * @returns Promise resolving to the event type
     */
    agreeToUPP(): Promise<boolean> {
        return this.getValue('agreeToUPP');
    }

    /**
     * ===== SETTER METHODS =====
     * Methods to update state properties
     * All setters return Promises to support async storage operations
     */

    /**
     * Sets the job type (one-time or ongoing).
     * @param type The job type to set
     * @returns Promise that resolves when the value is set
     */
    setJobType(type: JobTypes): Promise<void> {
        return this.setValue('jobType', type);
    }

    /**
     * Sets the start date of the job.
     * @param date The start date in ISO format (YYYY-MM-DD)
     * @returns Promise that resolves when the value is set
     */
    setStartDate(date: string | null): Promise<void> {
        return this.setValue('startDate', date);
    }

    /**
     * Sets the start time of the job.
     * @param value The start time in HH:MM format
     * @returns Promise that resolves when the value is set
     */
    setStartTime(value: string | null): Promise<void> {
        return this.setValue('startTime', value);
    }

    /**
     * Sets the end date of the job.
     * For one-time jobs, this may be the same as the start date.
     * @param date The end date in ISO format (YYYY-MM-DD)
     * @returns Promise that resolves when the value is set
     */
    setEndDate(date: string | null): Promise<void> {
        return this.setValue('endDate', date);
    }

    /**
     * Sets the number of guards required for the job.
     * @param guards The number of guards needed
     * @returns Promise that resolves when the value is set
     */
    setNumberOfGuards(guards: number | null): Promise<void> {
        return this.setValue('numberOfGuards', guards);
    }

    /**
     * Sets the number of hours each guard will work per day.
     * @param hours The number of hours per day
     * @returns Promise that resolves when the value is set
     */
    setNumberOfHoursPerDay(hours: number | null): Promise<void> {
        return this.setValue('numberOfHoursPerDay', hours);
    }

    /**
     * Sets the repeat cycle for ongoing jobs.
     * Defines how often the job repeats (daily, weekly, monthly, etc.).
     * @param value The repeat cycle type
     * @returns Promise that resolves when the value is set
     */
    setRepeatCycle(value: RepeatCycleTypes | null): Promise<void> {
        return this.setValue('repeatCycle', value);
    }

    /**
     * Sets the custom repeat type for jobs with custom repeat cycles.
     * Used when repeatCycle is set to 'custom'.
     * @param value The custom repeat type (days, weeks, months)
     * @returns Promise that resolves when the value is set
     */
    setRepeatCycleCustomType(value: RepeatCustomTypes | null): Promise<void> {
        return this.setValue('repeatCycleCustomType', value);
    }

    /**
     * Sets the days of the week for weekly repeating jobs.
     * @param value Array of day names
     * @returns Promise that resolves when the value is set
     */
    setRepeatDays(value: string[] | null): Promise<void> {
        return this.setValue('repeatDays', value);
    }

    /**
     * Sets the frequency amount for custom repeat cycles.
     * For example, "every 2 weeks" would have a frequency amount of 2.
     * @param value The frequency amount
     * @returns Promise that resolves when the value is set
     */
    setRepeatCycleFrequencyAmount(value: number | null): Promise<void> {
        return this.setValue('repeatCycleFrequencyAmount', value);
    }

    /**
     * Sets the end date for repeating jobs.
     * Defines when the recurring job schedule ends.
     * @param value The repeat end date in ISO format (YYYY-MM-DD)
     * @returns Promise that resolves when the value is set
     */
    setRepeatEndDate(value: string | null): Promise<void> {
        return this.setValue('repeatEndDate', value);
    }

    /**
     * Sets the event type for the job.
     * @param type The event type identifier
     * @returns Promise that resolves when the value is set
     */
    setEventType(type: string | null): Promise<void> {
        return this.setValue('eventType', type);
    }

    /**
     * Sets the location address for the job event.
     * @param address The complete address object with street, city, state, and zip
     * @returns Promise that resolves when the value is set
     */
    setEventLocationAddress(address: Address | null): Promise<void> {
        return this.setValue('eventLocationAddress', address);
    }

    /**
     * Sets the primary contact's name.
     * @param name The contact person's full name
     * @returns Promise that resolves when the value is set
     */
    setPrimaryContactName(name: string | null): Promise<void> {
        return this.setValue('primaryContactName', name);
    }

    /**
     * Sets the primary contact's phone number.
     * @param phone The contact person's phone number
     * @returns Promise that resolves when the value is set
     */
    setPrimaryContactPhone(phone: string | null): Promise<void> {
        return this.setValue('primaryContactPhone', phone);
    }

    /**
     * Sets the primary contact's email address.
     * @param email The contact person's email address
     * @returns Promise that resolves when the value is set
     */
    setPrimaryContactEmail(email: string | null): Promise<void> {
        return this.setValue('primaryContactEmail', email);
    }

    /**
     * Sets the job description.
     * @param text Detailed description of the job requirements
     * @returns Promise that resolves when the value is set
     */
    setJobDetails(text: string | null): Promise<void> {
        return this.setValue('jobDescription', text);
    }

    /**
     * Sets information about previous security issues at the location.
     * @param text Description of any previous security issues
     * @returns Promise that resolves when the value is set
     */
    setPreviousSecurityIssues(text: string | null): Promise<void> {
        return this.setValue('previousSecurityIssues', text);
    }

    /**
     * Sets emergency procedures for guards to follow.
     * @param text Instructions for handling emergency situations
     * @returns Promise that resolves when the value is set
     */
    setEmergencyProcedures(text: string | null): Promise<void> {
        return this.setValue('emergencyProcedures', text);
    }

    /**
     * Sets the primary duties expected of guards during the job.
     * @param text Description of main responsibilities and tasks
     * @returns Promise that resolves when the value is set
     */
    setGuardPrimaryDuties(text: string | null): Promise<void> {
        return this.setValue('guardPrimaryDuties', text);
    }

    /**
     * Sets whether uniforms are required for guards on this job.
     * @param value True if uniforms are required, false otherwise
     * @returns Promise that resolves when the value is set
     */
    setRequireUniform(value: boolean | null): Promise<void> {
        return this.setValue('requireUniform', value);
    }

    /**
     * Sets the type of uniform required for the job.
     * Only relevant if requireUniform is true.
     * @param value Description of the uniform type
     * @returns Promise that resolves when the value is set
     */
    setUniformType(value: string | null): Promise<void> {
        return this.setValue('uniformType', value);
    }

    /**
     * Sets who will provide the uniforms for the job.
     * Only relevant if requireUniform is true.
     * @param value The uniform provider option
     * @returns Promise that resolves when the value is set
     */
    setUniformProvidedBy(value: UniformProvidedByOptions | null): Promise<void> {
        return this.setValue('uniformProvidedBy', value);
    }

    /**
     * Sets whether the current navigation originated from the review screen.
     * Used to determine if the user should be returned to the review screen after editing.
     * @param value True if navigation is from review screen, false otherwise
     * @returns Promise that resolves when the value is set
     */
    setFromReviewScreen(value: boolean): Promise<void> {
        return this.setValue('fromReviewScreen', value);
    }

    /**
     * Sets if the user has agreed to the Terms of Use and Privacy Policy
     * @param value True if user has agreed, false if user has not agreed
     * @returns Promise that resolves when the value is set
     */
    setAgreeToUPP(value: boolean): Promise<void> {
        return this.setValue('agreeToUPP', value);
    }

    /**
     * Clears all job creation data.
     * Implementations should reset all state values to their defaults and
     * remove any persisted state from storage.
     *
     * @returns Promise that resolves when all data is cleared
     */
    abstract clearJobCreation(): Promise<void>;

    /**
     * Gets a value from the state by key.
     *
     * This method:
     * 1. Waits for state restoration to complete before returning any values
     * 2. Accesses the appropriate property using a naming convention (keyValue)
     * 3. Returns the value with proper typing
     *
     * The waiting mechanism ensures components don't receive null/default values
     * during the state restoration process.
     *
     * @param key The state property key to retrieve
     * @returns Promise resolving to the value of the specified property
     */
    protected async getValue<K extends StateKey>(key: K): Promise<StateSchema[K]> {
        // Access the property using the naming convention (keyValue)
        const value = this[`${key}Value` as keyof this];
        return value as unknown as StateSchema[K];
    }

    /**
     * Sets a value in the state by key.
     *
     * This method:
     * 1. Updates the in-memory property value
     * 2. Triggers the stateChange$ subject to initiate state persistence
     *
     * The stateChange$ subject is debounced to prevent excessive storage operations
     * when multiple properties are updated in quick succession.
     *
     * @param key The state property key to set
     * @param value The value to set
     * @returns Promise that resolves when the value is set
     */
    protected async setValue<K extends StateKey>(key: K, value: StateSchema[K]): Promise<void> {
        // Update the property using the naming convention (keyValue)
        const propertyKey = `${key}Value` as keyof this;
        (this[propertyKey] as unknown as StateSchema[K]) = value;
    }
}
